"use client";

import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stages = [{
  number: "STAGE 01",
  title: "initial consultation",
  description: "We begin by understanding your unique financial landscape. Our advisors conduct a deep-dive analysis of your current tax position, identifying immediate compliance needs and long-term strategic opportunities.",
  japanese: "初 期 相 談",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/tax_consultation.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/tax_consultation.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/tax_consultation.png-wvc-sizes",
  alt: "Professional tax advisor reviewing financial documents"
}, {
  number: "STAGE 02",
  title: "data aggregation",
  description: "Our team meticulously gathers and organizes your financial records. By centralizing your income statements, deductions, and investment data, we ensure a comprehensive foundation for accurate reporting.",
  japanese: "デ ー タ 集 約",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/data_aggregation.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/data_aggregation.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/data_aggregation.png-wvc-sizes",
  alt: "Digital financial data organization on a screen"
}, {
  number: "STAGE 03",
  title: "regulatory analysis",
  description: "We cross-reference your data against the latest tax codes and regulatory updates. This rigorous process ensures that every filing is compliant while uncovering potential tax-saving strategies.",
  japanese: "規 制 分 析",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/regulatory_analysis.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/regulatory_analysis.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/regulatory_analysis.png-wvc-sizes",
  alt: "Tax professional analyzing complex regulatory documents"
}, {
  number: "STAGE 04",
  title: "strategic planning",
  description: "Beyond simple filing, we develop a proactive roadmap for your financial future. We optimize your deductions and structure your assets to support long-term stability and growth.",
  japanese: "戦 略 的 計 画",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/strategic_planning.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/strategic_planning.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/strategic_planning.png-wvc-sizes",
  alt: "Strategic financial planning session with charts"
}, {
  number: "STAGE 05",
  title: "filing execution",
  description: "With precision and care, we prepare and submit your tax returns. Our team performs a final audit to ensure every detail is accurate, providing you with complete peace of mind.",
  japanese: "申 告 実 行",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/filing_execution.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/filing_execution.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/filing_execution.png-wvc-sizes",
  alt: "Finalizing tax return submission"
}, {
  number: "STAGE 06",
  title: "compliance review",
  description: "Post-filing, we conduct a thorough review to confirm all submissions meet regulatory standards. We maintain clear documentation, ensuring you are prepared for any future inquiries.",
  japanese: "コ ン プ ラ イ ア ン ス",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/compliance_review.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/compliance_review.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/compliance_review.png-wvc-sizes",
  alt: "Reviewing compliance documentation"
}, {
  number: "STAGE 07",
  title: "ongoing advisory",
  description: "Tax planning is a year-round commitment. We provide continuous support, adjusting your strategy as your life or business evolves, ensuring you remain on the path to financial clarity.",
  japanese: "継 続 的 助 言",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/ongoing_advisory.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/ongoing_advisory.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/ongoing_advisory.png-wvc-sizes",
  alt: "Ongoing financial advisory meeting"
}];
export default function TheLovejoyJourney() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      const node = timelineRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const totalScrollable = rect.height - windowHeight * 0.5;
      const scrolled = Math.min(Math.max(windowHeight * 0.5 - rect.top, 0), Math.max(totalScrollable, 1));
      const pct = Math.min(Math.max(scrolled / Math.max(totalScrollable, 1), 0), 1);
      setProgress(pct);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);
  return <section data-section-id="5132" id="the-lovejoy-journey" className="bg-background text-foreground py-20 md:py-32 px-6 md:px-12 lg:px-20 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className={`mb-20 md:mb-32 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="font-mono text-xs md:text-sm tracking-[0.3em] text-muted-foreground mb-8 uppercase">
            § 01 &mdash; The Lovejoy Journey
          </p>
          <h2 className="font-default text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-foreground max-w-5xl">
            Seven steps,
            <br />
            total clarity,
            <br />
            <span className="italic text-muted-foreground">your financial future.</span>
          </h2>
          <div className="mt-10 flex items-center gap-4 max-w-md">
            <div className="h-px flex-1 bg-border" />
            <p className="font-mono text-xs tracking-widest text-muted-foreground">TAX &middot; COMPLIANCE</p>
          </div>
        </div>

        <div ref={timelineRef} className="relative">
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-px bg-border" />
          <div className="hidden lg:block absolute left-1/2 top-0 -translate-x-1/2 w-px bg-accent transition-[height] duration-150 ease-out" style={{
          height: `${progress * 100}%`
        }} />

          <div className="lg:hidden absolute left-4 top-0 bottom-0 w-px bg-border" />
          <div className="lg:hidden absolute left-4 top-0 w-px bg-accent transition-[height] duration-150 ease-out" style={{
          height: `${progress * 100}%`
        }} />

          {stages.map((stage, i) => <TimelineStage key={i} data-index={i} stage={stage} index={i} isLast={i === stages.length - 1} />)}
        </div>
      </div>
    </section>;
}
function TimelineStage({
  stage,
  index,
  isLast
}: {
  stage: (typeof stages)[number];
  index: number;
  isLast: boolean;
  "data-index"?: number;
}) {
  const isLeft = index % 2 === 0;
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div ref={ref} data-index={index} className={`relative mb-20 md:mb-32 last:mb-0 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      <div className="hidden lg:block absolute left-1/2 top-12 -translate-x-1/2 z-10">
        <div className="w-3 h-3 rounded-lg bg-accent shadow-lg" />
        <div className="absolute inset-0 w-3 h-3 rounded-lg bg-accent animate-ping opacity-30" />
      </div>

      <div className="lg:hidden absolute left-4 top-6 -translate-x-1/2 z-10">
        <div className="w-3 h-3 rounded-lg bg-accent shadow-lg" />
      </div>

      <div className={`hidden lg:flex absolute top-12 ${isLeft ? "right-[calc(50%+2rem)]" : "left-[calc(50%+2rem)]"} items-start`} aria-hidden="true">
        <span className="font-default text-foreground/10 text-sm tracking-[0.4em]" style={{
        writingMode: "vertical-rl"
      }}>
          {stage.japanese}
        </span>
      </div>

      <div className="lg:grid lg:grid-cols-2 lg:gap-20 items-start pl-12 lg:pl-0">
        <div className={`mb-8 lg:mb-0 ${isLeft ? "lg:order-1 lg:pr-12" : "lg:order-2 lg:pl-12"}`}>
          <div className="relative overflow-hidden bg-muted aspect-[4/3]">
            <img src={stage.image} data-wvc-srcset={stage.srcset} data-wvc-sizes={stage.sizes} alt={stage.alt} loading="lazy" decoding="async" className="w-full h-full object-cover grayscale transition-all duration-1000 ease-out hover:grayscale-0 hover:scale-105" />
            <div className="absolute top-4 left-4 font-mono text-xs tracking-widest text-background bg-foreground/80 px-3 py-1.5 backdrop-blur-sm">
              {String(index + 1).padStart(2, "0")} / 07
            </div>
          </div>
        </div>

        <div className={`${isLeft ? "lg:order-2 lg:pl-12" : "lg:order-1 lg:pr-12 lg:text-right"}`}>
          <p className="font-mono text-sm md:text-base tracking-[0.3em] mb-6" style={{
          color: "var(--accent)"
        }}>
            {stage.number}
          </p>
          <h3 className="font-default text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground mb-8 lowercase">
            {stage.title}
          </h3>
          <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground max-w-xl lg:inline-block">
            {stage.description}
          </p>

          {isLast && <div className={`mt-12 flex ${isLeft ? "justify-start" : "lg:justify-end justify-start"}`}>
              <div className="relative inline-flex items-center justify-center w-24 h-24 md:w-28 md:h-28 border-2 border-accent rounded-lg rotate-[-6deg] bg-accent/10">
                <div className="absolute inset-1 border border-accent/60 rounded-lg" />
                <div className="text-center">
                  <p className="font-default text-accent text-lg md:text-xl leading-none" style={{
                writingMode: "vertical-rl"
              }}>
                    READY
                  </p>
                </div>
                <div className="absolute -bottom-2 -right-2 w-3 h-3 bg-accent rounded-lg" />
              </div>
            </div>}
        </div>
      </div>
    </div>;
}